import { AccountTree, Apartment, Delete, Folder, Home, HowToReg, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight, ManageAccounts, Share, Update, WatchLater, Workspaces } from '@mui/icons-material';
import { SelectChangeEvent } from "@mui/material";
import React, { MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Pendencie } from "../../assets/icons/pendencie";
import { useProfile } from "../../context/ProfileProvider/useProfile";
import { WorkSpace } from "../../model/WorkSpaceModel";
import { GetOneWorkSpaceFolderInformation, GetOrgWorkSpaceAdmTrim, GetOrgWorkSpaceTrim } from '../../services/WorkSpaceServices';
import { ContentbarProps, ISelectList } from "../Contentbar/ContentSide/ContentSideComp";
import { IUseContentBarRegularParameters } from "./sidebar";
import { navigationDocumentType } from "./utils/navigationToDocumentType";
import { navigationValidationRules } from "./utils/navigationValidationRules";
import { SidebarAllowedNavigate } from "./utils/SidebarAllowedNavigate";
import { SidebarOnChangeNavigationHandler } from "./utils/SidebarOnChangeNavigationHandler";
import { AddLastUrlPathLocalStorage, GetOrganizationLocalStorage, GetWorkspaceLocalStorage, } from "../../pages/PublicLayout/RedirectManager";
import { CurrentPage } from "../Contentbar/ContentSide/ContentSideList";
import useExtractPlan from '../../hooks/useExtractPlan';
import { KeydrivePlan } from '../../model/UserModel';
import { FolderInformation } from '../../model/ItemModel';
import { canViewAdminSection, canViewAdvancedWorkspaceFeatures, canViewDocuments, canViewFeed, canViewOrganizationAdvancedFeatures, canViewPrivileges, canViewRecycleBin, canViewShared, hasOrganization, VisibilityParams } from '../../utils/PermissionFunctions';
import { IPaginationItems } from '../../services/Pagination';
import { GetOrganizationWorkspaceAdmTrimName } from '../../services/OrganizationServices';
import { EnumPermissionTypeNone } from '../../enums/EnumPermissionTypeNone';

interface IContentStyles 
{ id: string, color: string }

export const useContentBarRegular = ({
  idWorkspace,
  isCollapsed,
  nameOrg,
  organizations,
  setIsCollapsed,
  fillColorSVG,
  organization,
  setOrganization,
  setWorkspace,
  workspace,
  navigateHome,
}: IUseContentBarRegularParameters) => {

  //#region States - useStates

  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<IPaginationItems<WorkSpace[]>>({ items: [], totalItems: 0 });
  const [anchorElOrganization, setAnchorElOrganization] = React.useState<null | HTMLElement>(null);
  const [anchorElWorkspace, setAnchorElWorkspace] = React.useState<null | HTMLElement>(null);
  const { adminSystem, organizations : profileOrgs } = useProfile();
  const profile = useProfile();
  const { t } = useTranslation();
  const workspaceLocalStorage = GetWorkspaceLocalStorage();
  const organizationLocalStorage = GetOrganizationLocalStorage();
  const [isAdminOrg, setIsAdminOrg] = useState<boolean>(false);
  const [isGuest, setIsGuest] = useState<boolean>(false);
  const [objectHovered, setObjectHovered] = useState<IContentStyles>({ color: "", id: "" });
  const [selected, setSelected] = useState<IContentStyles>({ color: "", id: "" });
  const location = useLocation();
  const {keydrivePlan} = useExtractPlan();
  const [folderInfo, setFolderInfo] = useState<FolderInformation>({} as FolderInformation);
  const [loadingFolderInfo, setLoadingFolderInfo] = useState(true);

  useEffect(() => {
    IsUserOrgAdmin();
  }, [profileOrgs]);

  useEffect(() => {
    IsUserGuest();
  }, [profileOrgs])

  useEffect(() => {
    if (idWorkspace != undefined) {
      GetOneWorkSpaceFolderInformationAsync();
    }
  }, [idWorkspace]);

  useEffect(() => {
    if (idWorkspace !== undefined) {
      GetWorkspaceAboutOrganizationState(nameOrg!);
      setWorkspace(idWorkspace);
    } else {
      setWorkspace("");
    }

    if (nameOrg !== undefined) {
      setOrganization(nameOrg!);
      GetWorkspaceAboutOrganizationState(nameOrg!)
    } else {
      setOrganization("");
    }
 
    profile.organizations?.forEach(org => {
      if (org.internalName == nameOrg){
        if (org.isAdmin !== undefined) {
          setIsAdminOrg(org.isAdmin);
        }
      }
    })
  }, [idWorkspace, nameOrg]);

  useEffect(() => {
    if (organizationLocalStorage !== null && workspaceLocalStorage != null) {
      setOrganization(organizationLocalStorage);
      setWorkspace(workspaceLocalStorage);
      GetWorkspaceAboutOrganizationState(organizationLocalStorage);
    } else if (organizationLocalStorage != null && workspaceLocalStorage == null) {
      setOrganization(organizationLocalStorage);
    }

  }, [workspaceLocalStorage, organizationLocalStorage]);

  useEffect(() => {
    if (idWorkspace) {
      GetWorkspaceAboutOrganizationState(organizationLocalStorage!)
    }
  }, [location.pathname])

  // #endregion

  //#region Functions  

  const openOrganization = Boolean(anchorElOrganization);

  const IsUserGuest = (): void => {
    if (profileOrgs != undefined) {
      for(let organization of profileOrgs)
      {
        if(nameOrg == organization.internalName)
        {
          if(organization.isGuest)
          {
            setIsGuest(true);
          }
          break;
        }
      }
    }
  }

  const IsUserOrgAdmin = (): void => {
    if(profileOrgs != undefined)
    {
      for(let organization of profileOrgs)
      {
        if(nameOrg == organization.internalName)
        {
          if(organization.isAdmin == true)
          {
            setIsAdminOrg(true);
          }
          break;
        }
      }
    }
  }

  const handleClickOrganization = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElOrganization(event.currentTarget);
  };

  const handleCloseOrganization = () => {
    setAnchorElOrganization(null);
  };

  const openWorkspace = Boolean(anchorElWorkspace);

  const handleClickWorkspace = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElWorkspace(event.currentTarget);
  };

  const handleCloseWorkspace = () => {
    setAnchorElWorkspace(null);
  };

  async function GetOneWorkSpaceFolderInformationAsync() {
    setLoadingFolderInfo(true);
    try {
      const response = await GetOneWorkSpaceFolderInformation(idWorkspace);
      setFolderInfo(response);
    } catch {
      
    } finally {
      setLoadingFolderInfo(false);
    }
  }

  function GetWorkspaceAboutOrganizationState(organizationState: string) {

    GetOrganizationWorkspaceAdmTrimName(organizationState, 0, 11, false, "")
    .then(data => {
        setWorkspaces({ items: data.items, totalItems: data.totalItems });
      })
      .catch(err => {
        setWorkspaces({ items: [], totalItems: 0 });
      });
      
  }

  function onChangeOrganizationSelection(selectEvent: SelectChangeEvent | undefined, mouseEvent?: MouseEvent<HTMLLIElement, globalThis.MouseEvent>) {
    SidebarOnChangeNavigationHandler("organization", selectEvent, setOrganization, setWorkspace, GetWorkspaceAboutOrganizationState, mouseEvent);
  }

  function onChangeWorkspaceSelection(selectEvent: SelectChangeEvent | undefined, mouseEvent?: MouseEvent<HTMLLIElement, globalThis.MouseEvent>) {
    SidebarOnChangeNavigationHandler("workspace", selectEvent, setOrganization, setWorkspace, GetWorkspaceAboutOrganizationState, mouseEvent);
  }

  function navigateAdminConsolePage() {
    AddLastUrlPathLocalStorage(location.pathname);
    navigate(`/${nameOrg}/adminConsole`);
  }

  function handleStateStyle(seletecdStyles: IContentStyles, hoveredStyles: IContentStyles, contentChildren: string) : string
  {
    if (hoveredStyles.id != "")
    {
      return objectHovered.id == contentChildren ? objectHovered.color : "#000000"
    } else
    {
      return seletecdStyles.id == contentChildren ? seletecdStyles.color : "#000000"
    }
  }

  function renderPermissionOption() : boolean {
    if (profile?.adminSystem) {
      return true;
    }

    if (isAdminOrg) {
      return true;
    }

    if (folderInfo?.folderOwnerInformation?.id == profile.id) {
      return true;
    }

    if (keydrivePlan !== KeydrivePlan.FREE && (profile?.adminSystem || isAdminOrg || folderInfo?.folderOwnerInformation?.id == profile.id)) {
      return true;
    }
    
    return false;
  }

  // #endregion

  const visibilityParams: VisibilityParams = {
    organization,
    workspace,
    isAdminOrg: isAdminOrg!,
    adminSystem: adminSystem!,
    keydrivePlan: keydrivePlan!,
    folderInfo,
    loadingFolderInfo,
    profile,
    isGuest: isGuest
  };

  const contentBar: ContentbarProps<ISelectList>[] = [
  {
    children: t("Sidebar.Organizations"),
    isSelect: false,
    footer: false,
    icon: <Apartment sx={{ color: "#000000" }} />,
    isCollapsed: isCollapsed,
    onClick: () => { },
    title: t("Sidebar.Organizations"),
    isSelectField: true,
    anchorElSelect: anchorElOrganization,
    handleClickSelect: handleClickOrganization,
    handleCloseSelect: handleCloseOrganization,
    nameOrg: nameOrg!,
    onChangeSelection: onChangeOrganizationSelection,
    openSelect: openOrganization,
    selectList: organizations.map(org => ({
      id: org.id!,
      internalName: org.internalName!,
      name: org.name!
    })),
    selectValue: organization,
    disabled: false,
    renderOption: true,
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected,
    currentPage: CurrentPage.NONE,
    isWorkspace: false
  },
  {
    children: t("Sidebar.Home"),
    isSelect: false,
    footer: false,
    icon: <Home width={30} height={30} opacity={hasOrganization(organization) ? 1 : 0.8} />,
    isCollapsed: isCollapsed,
    onClick: (e) => SidebarAllowedNavigate(`/${nameOrg}`, false, e, organization, workspace, navigate),
    title: t("Sidebar.Home"),
    isSelectField: false,
    disabled: false,
    renderOption: hasOrganization(organization),
    currentPage: CurrentPage.HOME,
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected
  },
  {
    children: t("Sidebar.Feed"),
    isSelect: false,
    footer: false,
    icon: <Update sx={{color: handleStateStyle(selected, objectHovered, t("Sidebar.Feed"))}}/>,
    isCollapsed: isCollapsed,
    onClick: (e) => SidebarAllowedNavigate(`/${nameOrg}/Item/tracker`, false, e, organization, workspace, navigate),
    title: t("Sidebar.Feed"),
    isSelectField: false,
    disabled: false,
    renderOption: canViewOrganizationAdvancedFeatures(visibilityParams) && canViewFeed(visibilityParams),
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected,
    currentPage: CurrentPage.FEED,
  },
  {
    children: t("SharedItem.Title"),
    isSelect: false,
    footer: false,
    icon: <Share/>,
    isCollapsed: isCollapsed,
    onClick: (e) => SidebarAllowedNavigate(`/${nameOrg}/SharedItem`, false, e, organization, workspace, navigate),
    title: t("SharedItem.Title"),
    isSelectField: false,
    disabled: false,
    renderOption: canViewShared(organization),
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected,
    currentPage: CurrentPage.SHAREDITEM,
  },
  {
    children: t("RecicleBin.title"),
    isSelect: false,
    footer: false,
    icon: <Delete sx={{color: handleStateStyle(selected, objectHovered, t("RecicleBin.title"))}}/>,
    isCollapsed: isCollapsed,
    onClick: (e) => SidebarAllowedNavigate(`/${nameOrg}/recycleBin`, false, e, organization, workspace, navigate),
    title: t("RecicleBin.title"),
    isSelectField: false,
    disabled: false,
    renderOption: canViewRecycleBin(visibilityParams),
    currentPage: CurrentPage.RECICLE_BIN,
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected
  },
  {
    children: "Workspaces",
    isSelect: false,
    footer: false,
    icon: <Workspaces sx={{ color: "#000000" }} />,
    isCollapsed: isCollapsed,
    onClick: () => { },
    title: "Workspaces",
    isSelectField: true,
    anchorElSelect: anchorElWorkspace,
    handleClickSelect: handleClickWorkspace,
    handleCloseSelect: handleCloseWorkspace,
    nameOrg: nameOrg!,
    onChangeSelection: onChangeWorkspaceSelection,
    openSelect: openWorkspace,
    selectList: workspaces?.items?.map(ws => ({
      id: ws.id!,
      internalName: undefined,
      name: ws.name!
    })),
    selectValue: workspace,
    disabled: false,
    renderOption: hasOrganization(organization),
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected,
    currentPage: CurrentPage.NONE,
    isWorkspace: true
  },
  {
    children: t("Sidebar.Documents"),
    isSelect: false,
    footer: false,
    icon: <Folder sx={{color: handleStateStyle(selected, objectHovered, t("Sidebar.Documents"))}}/>,
    isCollapsed: isCollapsed,
    onClick: (e) => {
      const currentWorkspace = idWorkspace || workspaceLocalStorage;
      const currentOrg = nameOrg || organizationLocalStorage;
      
      SidebarAllowedNavigate(
        navigationDocumentType(currentWorkspace!, currentOrg!, currentOrg!), 
        true, 
        e, 
        organization, 
        workspace, 
        navigate
      );
    },    
    title: t("Sidebar.Documents"),
    isSelectField: false,
    disabled: false,
    renderOption: canViewDocuments(organization, workspace),
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected,
    currentPage: CurrentPage.DOCUMENTS
  },
  {
    children: t("Sidebar.Pendencies"),
    isSelect: false,
    footer: false,
    icon: <Pendencie colorCircle={fillColorSVG()} />,
    isCollapsed: isCollapsed,
    onClick: (e) => SidebarAllowedNavigate(`/${nameOrg}/${workspace}/UserPendencies`, false, e, organization, workspace, navigate),
    title: t("Sidebar.Pendencies"),
    isSelectField: false,
    disabled: false,
    renderOption: canViewAdvancedWorkspaceFeatures(visibilityParams),
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected,
    currentPage: CurrentPage.PENDENCIES,
  },
  {
    children: t("Sidebar.Privileges"),
    isSelect: false,
    footer: false,
    icon: <HowToReg sx={{color: handleStateStyle(selected, objectHovered, t("Sidebar.Privileges"))}}/>,
    isCollapsed: isCollapsed,
    onClick: (e) => SidebarAllowedNavigate(`/${nameOrg}/${idWorkspace}/Permission`, false, e, organization, workspace, navigate),
    title: t("Sidebar.Privileges"),
    isSelectField: false,
    disabled: false,
    renderOption: canViewPrivileges(visibilityParams) && renderPermissionOption(),
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected,
    currentPage: CurrentPage.PRIVILEGES,
  },
  {
    children: t("Sidebar.ValidationRules"),
    isSelect: false,
    footer: false,
    icon: <AccountTree sx={{color: handleStateStyle(selected, objectHovered, t("Sidebar.ValidationRules"))}}/>,
    isCollapsed: isCollapsed,
    onClick: (e) => {
      const currentWorkspace = idWorkspace || workspaceLocalStorage;
      const currentOrg = nameOrg || organizationLocalStorage;
      
      SidebarAllowedNavigate(
        navigationValidationRules(currentWorkspace!, currentOrg!, currentOrg!), 
        true, 
        e, 
        organization, 
        workspace, 
        navigate
      );
    },    
    title: t("Sidebar.ValidationRules"),
    isSelectField: false,
    disabled: false,
    renderOption: canViewAdvancedWorkspaceFeatures(visibilityParams) && folderInfo?.permissionType != EnumPermissionTypeNone.edit,
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected,
    currentPage: CurrentPage.VALIDATIONRULES,
  },
  {
    children: t("Sidebar.Temporality"),
    isSelect: false,
    footer: false,
    icon: <WatchLater sx={{color: handleStateStyle(selected, objectHovered, t("Sidebar.Temporality"))}}/>,
    isCollapsed: isCollapsed,
    onClick: (e) => SidebarAllowedNavigate(`/${nameOrg}/${workspace}/temporality`, true, e, organization, workspace, navigate),
    title: t("Sidebar.Temporality"),
    isSelectField: false,
    disabled: false,
    renderOption: canViewAdvancedWorkspaceFeatures(visibilityParams) && folderInfo?.permissionType != EnumPermissionTypeNone.edit,
    currentPage: CurrentPage.TEMPORALITY,
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected
  },
  {
    children: "Admin Console",
    isSelect: false,
    footer: true,
    icon: <ManageAccounts />,
    isCollapsed: isCollapsed,
    onClick: (e) => navigateAdminConsolePage(),
    title: "Admin console",
    isSelectField: false,
    disabled: false,
    renderOption: canViewAdminSection(organization, isAdminOrg!, adminSystem!),
    currentPage: CurrentPage.ADMIN,
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected
  },
  {
    children: t("Sidebar.CloseSidebar"),
    isSelect: false,
    footer: true,
    icon: isCollapsed ? <KeyboardDoubleArrowRight /> : <KeyboardDoubleArrowLeft />,
    isCollapsed: isCollapsed,
    onClick: () => {
      setIsCollapsed(!isCollapsed)
      localStorage.setItem("persistedCollapsedSidebar", String(!isCollapsed))
    },
    title: t("Sidebar.CloseSidebar"),
    isSelectField: false,
    disabled: false,
    renderOption: true,
    isNameOption: false,
    setHovered: setObjectHovered,
    setSelected: setSelected,
    currentPage: CurrentPage.NONE,
  }
]

  return {
    contentBar,
    organization,
    workspace,
  }
}

